import { Box, Typography, Button, Card, CardContent, Grid, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { StatusBadge, LoadingSpinner } from '@/shared/components';
import { useJobQuery } from '@/features/jobs';

interface TranslationResultsProps {
  jobId: string;
  onNewTranslation: () => void;
}

export default function TranslationResults({ jobId, onNewTranslation }: TranslationResultsProps) {
  const { data: job, isLoading } = useJobQuery(jobId);

  if (isLoading || !job) {
    return <LoadingSpinner message="Cargando resultados..." />;
  }

  const successCount = job.results.filter((r) => !r.error).length;
  const errorCount = job.results.filter((r) => r.error).length;
  const isCancelled = job.status === 'cancelled';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Resultados</Typography>
        <StatusBadge status={job.status} />
      </Box>

      {/* Summary */}
      <Card variant="outlined" sx={{ mb: 3, backgroundColor: 'grey.50' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Resumen {isCancelled && '(Proceso cancelado)'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Total procesadas
              </Typography>
              <Typography variant="h6">
                {job.completed} de {job.total}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Exitosas
              </Typography>
              <Typography variant="h6" color="success.main">
                {successCount}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Con errores
              </Typography>
              <Typography variant="h6" color="error.main">
                {errorCount}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {job.results.map((result, index) => (
          <Card
            key={index}
            variant="outlined"
            sx={{
              borderLeftWidth: 4,
              borderLeftColor: result.error ? 'error.main' : 'success.main',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {result.error ? (
                  <ErrorIcon color="error" fontSize="small" />
                ) : (
                  <CheckCircleIcon color="success" fontSize="small" />
                )}
                <Typography variant="subtitle2">
                  {result.modelId || `URL ${index + 1}`}
                </Typography>
              </Box>

              {result.error ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {result.error}
                </Alert>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Versión: {result.version} | Idioma: {result.targetLang}
                  </Typography>
                  {result.reportFilename && (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1 }}
                      href={`/api/v1/jobs/${jobId}/reports/${result.reportFilename}`}
                      target="_blank"
                    >
                      Ver Reporte
                    </Button>
                  )}
                </Box>
              )}

              {result.url && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}
                >
                  {result.url}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      <Button variant="contained" onClick={onNewTranslation}>
        Nueva Traducción
      </Button>
    </Box>
  );
}
